// src/utils/searchKeywordUtils.ts
// Search keyword translation utilities
// Extracted from App.js - no React dependencies

/** Korean skin concern keywords */
export type KoreanKeyword =
  | '모공'
  | '여드름'
  | '블랙헤드'
  | '색소침착'
  | '주름'
  | '다크서클'
  | '눈밑지방'
  | '모공축소'
  | '주름개선'
  | '아이크림'
  | '지성'
  | '건성'
  | '중성'
  | '복합성';

/** Keyword translation mapping */
const translations: Record<KoreanKeyword, string[]> = {
  '모공': ['pore', 'pores', 'pore-care'],
  '여드름': ['acne', 'acne-care', 'blemish'],
  '블랙헤드': ['blackhead', 'blackheads'],
  '색소침착': ['dark spot', 'pigmentation', 'brightening'],
  '주름': ['wrinkle', 'wrinkles', 'anti-aging', 'firming'],
  '다크서클': ['dark circle', 'dark circles', 'eye cream'],
  '눈밑지방': ['eye bag', 'eye bags', 'under eye'],
  '모공축소': ['pore minimizer', 'pore-care', 'pore'],
  '주름개선': ['anti-aging', 'wrinkle', 'firming'],
  '아이크림': ['eye cream', 'eye care'],
  '지성': ['oily'],
  '건성': ['dry'],
  '중성': ['normal'],
  '복합성': ['combo', 'combination'], // 'combo' is primary to match catalog
};

/**
 * Translate Korean keywords to English for backward compatibility with catalog matching
 * @param koreanKeyword - Korean skin concern or skin type keyword
 * @returns Array of English translations/synonyms
 */
export const translateKeywordToEnglish = (koreanKeyword: string): string[] => {
  return translations[koreanKeyword as KoreanKeyword] || [];
};

/**
 * Translate English keyword/synonym back to supported Korean keywords
 * - Performs case-insensitive match
 * - Normalizes common punctuation (hyphens)
 * @param englishKeyword - English keyword or synonym
 * @returns Array of matching Korean keywords (may be empty)
 */
export const translateKeywordToKorean = (englishKeyword: string): KoreanKeyword[] => {
  if (!englishKeyword || typeof englishKeyword !== 'string') return [];

  const normalized = englishKeyword.trim().toLowerCase().replace(/[-_]+/g, ' ');

  const matches: Set<KoreanKeyword> = new Set();

  (Object.keys(translations) as KoreanKeyword[]).forEach((korean) => {
    const englishList = translations[korean];
    for (const eng of englishList) {
      if (eng.toLowerCase() === normalized || eng.toLowerCase().includes(normalized) || normalized.includes(eng.toLowerCase())) {
        matches.add(korean);
        break;
      }
    }
  });

  return Array.from(matches);
};

/**
 * Get all supported Korean keywords
 * @returns Array of supported Korean keywords
 */
export const getSupportedKoreanKeywords = (): KoreanKeyword[] => [
  '모공',
  '여드름',
  '블랙헤드',
  '색소침착',
  '주름',
  '다크서클',
  '눈밑지방',
  '모공축소',
  '주름개선',
  '아이크림',
  '지성',
  '건성',
  '중성',
  '복합성',
];

/**
 * Check if a keyword is a supported Korean keyword
 * @param keyword - Keyword to check
 * @returns True if the keyword is supported
 */
export const isKoreanKeyword = (keyword: string): keyword is KoreanKeyword => {
  return keyword in translations;
};
